import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, MaskedLine, Eyebrow, EASE_OUT } from "@/components/Reveal";

const CHAPTERS = [
  {
    id: "executive-search",
    title: "Executive Search",
    tagline: "Finding leaders who can shape the business.",
    intro:
      "Our Executive Search practice focuses on identifying senior professionals who bring more than functional expertise. We assess leadership capability, business context, industry relevance, transformation experience, cultural alignment and long-term potential.",
    sections: [
      {
        heading: "We support searches for",
        items: ["CXO & C-Suite", "Business Heads", "Country Heads", "Functional Heads", "Technology Leadership", "Product Leadership", "Sales & Revenue Leadership", "Engineering Leadership", "HR Leadership", "Finance Leadership", "Marketing Leadership", "Operations Leadership", "Digital Transformation Leaders"],
      },
    ],
    note: "Our approach — Understand → Map → Identify → Assess → Engage → Deliver. Every executive mandate begins with understanding the business challenge behind the position.",
  },
  {
    id: "retained-search",
    title: "Retained Search",
    tagline: "Strategic search for critical leadership positions.",
    intro:
      "Some appointments require greater depth, confidentiality and market mapping. Our Retained Search model is designed for critical leadership positions where organizations need a dedicated search partner rather than a traditional recruitment vendor.",
    sections: [
      {
        heading: "Retained Search includes",
        items: ["Detailed mandate discovery", "Leadership competency mapping", "Target-company mapping", "Market intelligence", "Confidential candidate identification", "Executive approach & engagement", "Structured candidate assessment", "Compensation intelligence", "Reference & background coordination", "Search progress reporting", "Offer & closure support", "Post-placement follow-up"],
      },
    ],
    note: "Ideal for — CXO · Business Heads · Country Leaders · Technology Leaders · Product Leaders · Critical Strategic Positions.",
  },
  {
    id: "permanent-hiring",
    title: "Permanent Hiring",
    tagline: "Build teams that move the business forward.",
    intro:
      "We don't believe in CV flooding. Our Permanent Hiring practice supports mid-level, specialist and senior hiring — identifying candidates relevant to the role, organization, technology environment and business context.",
    sections: [
      { heading: "Technology", items: ["Software Engineering", "Architecture", "Cloud & DevOps", "Data & Analytics", "AI & ML", "Cybersecurity", "QA & Testing", "Engineering Management"] },
      { heading: "Product", items: ["Product Management", "Product Strategy", "Product Operations", "UX / UI", "Product Engineering"] },
      { heading: "Business", items: ["Sales", "Business Development", "Customer Success", "Operations", "Finance", "HR", "Marketing"] },
      { heading: "Specialist Domains", items: ["InsurTech", "MarTech", "AdTech", "SaaS", "FinTech", "Healthcare Technology"] },
    ],
    note: "Relevance over volume — every profile is evaluated against your business context.",
  },
  {
    id: "contract-staffing",
    title: "Contract & Staff Augmentation",
    tagline: "Flexible talent. Faster scale.",
    intro:
      "For project-based requirements, transformation programs, temporary capacity gaps or rapid scaling, our Contract Hiring and IT Staff Augmentation solutions provide specialized talent without long-term hiring commitments.",
    sections: [
      {
        heading: "We support",
        items: ["Contract professionals", "Contract-to-hire", "Project-based hiring", "Technology staff augmentation", "Specialized consultants", "Interim professionals", "Short-term leadership requirements"],
      },
    ],
    note: "Designed for — Project launches · Digital transformation · Technology modernization · Product development · GCC ramp-ups · Temporary capacity requirements.",
  },
  {
    id: "hr-advisory",
    title: "HR Consulting & Advisory",
    tagline: "Building the HR foundation behind growing businesses.",
    intro:
      "Startups and growing companies often need HR expertise before they need a full-scale HR department. We provide practical HR advisory support designed around your stage of growth.",
    sections: [
      { heading: "HR Strategy", items: ["HR roadmap", "Organization design", "Workforce planning", "Talent strategy", "HR operating model"] },
      { heading: "Startup HR", items: ["HR foundation", "HR policies", "Employee lifecycle", "HR processes", "Documentation", "Compliance coordination"] },
      { heading: "Talent Management", items: ["Competency frameworks", "Career architecture", "Succession planning", "Performance management", "Leadership development"] },
      { heading: "Compensation & Benchmarking", items: ["Salary benchmarking", "Compensation structures", "Offer positioning", "Market intelligence", "Incentive frameworks"] },
      { heading: "HR Process & Operations", items: ["HR process design", "Recruitment process", "Onboarding", "Employee policies", "HR workflow improvement"] },
    ],
    note: "Practical advisory, designed around your stage of growth.",
  },
  {
    id: "startup-hr",
    title: "Startup HR Services",
    tagline: "Your HR partner before you build an HR department.",
    intro:
      "Startups need speed, but they also need structure. We help founders build the HR foundation required to attract talent, manage people and scale sustainably — from founder-led hiring to a scalable people function.",
    sections: [
      { heading: "Build", items: ["HR policies", "Employment documentation", "HR processes", "Organization structures"] },
      { heading: "Hire", items: ["Leadership hiring", "Core team hiring", "Technology hiring", "Specialist hiring"] },
      { heading: "Structure", items: ["Job descriptions", "Salary bands", "Competency frameworks", "Performance systems"] },
      { heading: "Scale", items: ["Workforce planning", "Talent strategy", "HR operations", "Employee lifecycle"] },
    ],
    note: "From founder-led hiring to a scalable people function.",
  },
  {
    id: "training",
    title: "Training & Leadership Development",
    tagline: "Developing people who build businesses.",
    intro:
      "Our training solutions are designed around practical workplace requirements rather than generic classroom programs.",
    sections: [
      { heading: "Recruitment & Talent Acquisition", items: ["Interviewing skills", "Candidate assessment", "Recruitment best practices", "Talent sourcing", "Hiring manager collaboration"] },
      { heading: "Leadership", items: ["First-time manager development", "Leadership effectiveness", "Executive presence", "People management"] },
      { heading: "HR", items: ["Strategic HR", "Talent management", "Performance management", "HR operations"] },
      { heading: "Business Skills", items: ["Communication", "Stakeholder management", "Negotiation", "Workplace effectiveness"] },
    ],
    note: "Delivered through — Workshops · Leadership Programs · Customized Corporate Training · Consulting-led Programs.",
  },
];

const MODELS = [
  { title: "Executive Search", best: "CXO, Business Heads, Functional Leaders", desc: "For critical leadership appointments." },
  { title: "Retained Search", best: "Mission-critical leadership positions", desc: "Dedicated partnership for strategic and confidential mandates." },
  { title: "Permanent Hiring", best: "Scaling teams and specialist hiring", desc: "End-to-end recruitment for mid to senior-level positions." },
  { title: "Contract Hiring", best: "Transformation, projects and rapid scaling", desc: "Flexible talent for projects and temporary requirements." },
  { title: "HR Advisory", best: "Startups, scale-ups and growing organizations", desc: "Strategic and operational HR support." },
  { title: "Training", best: "HR teams, managers and organizations", desc: "Customized learning and leadership programs." },
];

const Services = () => {
  const [active, setActive] = useState(CHAPTERS[0].id);
  const chapter = CHAPTERS.find((c) => c.id === active);

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="border-b border-line pt-36 lg:pt-44" data-testid="services-hero">
        <div className="mx-auto max-w-7xl px-6 pb-16 lg:px-10 lg:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <Eyebrow>Our Services</Eyebrow>
          </motion.div>
          <h1 className="mt-6 max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>Hiring a CEO, scaling a team,</MaskedLine>
            <MaskedLine delay={0.4}>
              or building your <span className="italic text-gold-dark">HR foundation.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE_OUT }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-ink-muted sm:text-lg"
          >
            Whatever the mandate, Ally eXecutive HR has a solution designed
            around your requirement — seven practices, one standard of rigor.
          </motion.p>
        </div>
      </section>

      {/* CHAPTER SELECTOR */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28" data-testid="services-chapters-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-faded">Service Chapters</p>
              <div className="mt-6 flex flex-col" role="tablist" aria-label="Service chapters">
                {CHAPTERS.map((c, i) => (
                  <button
                    key={c.id}
                    role="tab"
                    aria-selected={active === c.id}
                    onClick={() => setActive(c.id)}
                    data-testid={`service-chapter-tab-${c.id}`}
                    className={`group flex items-baseline gap-4 border-b border-line py-4 text-left transition-colors duration-300 ${
                      active === c.id ? "text-ink" : "text-ink-faded hover:text-ink-muted"
                    }`}
                  >
                    <span className={`font-mono text-xs ${active === c.id ? "text-gold-dark" : "text-line-medium"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className={`font-serif text-lg transition-transform duration-300 ${active === c.id ? "translate-x-1" : ""}`}>
                      {c.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={chapter.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: EASE_OUT }}
                className="border border-line-medium bg-paper-card p-8 lg:p-12"
                data-testid="service-chapter-panel"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-dark">{chapter.tagline}</p>
                <h2 className="mt-4 font-serif text-2xl tracking-tight text-ink sm:text-3xl">{chapter.title}</h2>
                <p className="mt-5 text-base leading-relaxed text-ink-muted">{chapter.intro}</p>
                <div className="mt-10 grid gap-8 sm:grid-cols-2">
                  {chapter.sections.map((sec) => (
                    <div key={sec.heading}>
                      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">{sec.heading}</p>
                      <ul className="mt-4 space-y-2">
                        {sec.items.map((item) => (
                          <li key={item} className="flex items-start gap-3 text-sm text-ink-muted">
                            <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="mt-10 border-t border-line pt-6 font-serif text-base italic text-ink">{chapter.note}</p>
                <Link
                  to="/about-contact#contact"
                  className="group mt-8 inline-flex items-center gap-2 border border-ink bg-ink px-6 py-3 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-gold hover:border-gold hover:text-ink"
                  data-testid="service-chapter-discuss-button"
                >
                  Discuss This Requirement
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODELS */}
      <section className="border-y border-line bg-paper-deep py-20 lg:py-28" data-testid="engagement-models-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Eyebrow>Engagement Models</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Choose the partnership that fits your requirement.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-px border border-line-medium bg-line-medium md:grid-cols-2 lg:grid-cols-3">
            {MODELS.map((m, i) => (
              <FadeUp key={m.title} delay={0.05 * i} className="group bg-paper p-8 transition-colors duration-500 hover:bg-ink">
                <h3 className="font-serif text-xl text-ink transition-colors duration-500 group-hover:text-paper">{m.title}</h3>
                <p className="mt-3 text-sm text-ink-muted transition-colors duration-500 group-hover:text-paper/60">{m.desc}</p>
                <p className="mt-6 border-t border-line pt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-gold-dark transition-colors duration-500 group-hover:border-paper/15 group-hover:text-gold">
                  Best for — {m.best}
                </p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-20 text-center lg:px-10 lg:py-28" data-testid="services-cta-section">
        <FadeUp>
          <h2 className="mx-auto max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Tell us about <span className="italic text-gold-dark">your requirement.</span>
          </h2>
          <Link
            to="/about-contact#contact"
            className="group mt-9 inline-flex items-center gap-2 border border-ink bg-ink px-8 py-4 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-gold hover:border-gold hover:text-ink"
            data-testid="services-cta-button"
          >
            Start a Conversation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </FadeUp>
      </section>
    </div>
  );
};

export default Services;
